import { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItemText,
  MenuItem,
  styled,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CountUpdateCard from './CountUpdateCard';
import CallDurationUpdateCard from './CallDurationUpdateCard';
import IvrUpdateCard from './IvrUpdateCard';
import CampaignDateTimeUpdateCard from './CampaignDateTimeUpdateCard';
import DtmfUpdateCard from './DtmfUpdateCard';
import UserSelectCard from './UserSelectCard';
import { axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { checkButtonAccess } from 'src/utils/common';

const InputContainer = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  gap: '20px',
}));

const InputInnerContainer = styled('div')(() => ({
  flex: 1,
}));

const ActionTypeOptions = [
  { label: 'IVR Update', value: 'ivrUpdate' },
  { label: 'Campaign Date & Time', value: 'campaignDateTime' },
  { label: 'Call Duration', value: 'callDuration' },
  { label: 'DTMF Update', value: 'dtmfUpdate' },
  { label: 'Update Answer Count', value: 'answer' },
  { label: 'Update Not Answer Count', value: 'unanswer' },
  { label: 'Update Busy Count', value: 'busy' },
  { label: 'Update Other Count', value: 'other' },
];

const formatDateTime = (val) => {
  if (!val) return null;
  if (typeof val?.toISOString === 'function') {
    return val.toISOString();
  }
  const date = new Date(val);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

// Count action types that share the global countMode toggle
const COUNT_ACTION_TYPES = ['answer', 'unanswer', 'busy', 'other'];

export default function AdminSettingForm() {
  const theme = useTheme();
  const [loader, setLoader] = useState(false);
  const [resetloader, setResetLoader] = useState(false);

  // Global count mode: 'count' | 'percent'
  const [countMode, setCountMode] = useState('count');

  // Selected IVR from IvrUpdateCard — needed by DtmfUpdateCard
  const [selectedIvr, setSelectedIvr] = useState(null);

  // Selected User
  const [selectedUser, setSelectedUser] = useState(null);
  const [userError, setUserError] = useState('');

  // Global "update existing records" flag
  const [updateExistingRecords, setUpdateExistingRecords] = useState(false);

  const optionalNumber = Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .nullable();

  // Builds a count field schema that conditionally applies max: 100 when in percent mode.
  // NOTE: countMode is a closure variable captured at schema-build time;
  // we rebuild the schema when countMode changes via the resolver key trick.
  const countField = (fieldName, actionKey) =>
    optionalNumber.when('actionType', {
      is: (val) => val?.includes(actionKey),
      then: (s) => {
        let base = s.required(`${fieldName} is required`).typeError('Must be a number').min(0);
        if (countMode === 'percent') {
          base = base.max(100, `${fieldName} cannot exceed 100 in percent mode`);
        }
        return base;
      },
      otherwise: (s) => s.nullable(),
    });

  const schema = Yup.object().shape({
    campaignIds: Yup.string().required('At least one Campaign ID is required'),
    actionType: Yup.array().min(1, 'At least one action type is required'),
    // Answer
    answerCount: countField('Answer count', 'answer'),
    // Not Answer
    notAnswerCount: countField('Not answer count', 'unanswer'),
    // Busy
    busyCount: countField('Busy count', 'busy'),
    // Other
    otherCount: countField('Other count', 'other'),
    // Call Duration
    callDurationMin: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .when('actionType', {
        is: (val) => val?.includes('callDuration'),
        then: (s) =>
          s
            .required('Min duration is required')
            .typeError('Must be a number')
            .min(0),
      }),
    callDurationMax: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .when('actionType', {
        is: (val) => val?.includes('callDuration'),
        then: (s) =>
          s
            .required('Max duration is required')
            .typeError('Must be a number')
            .moreThan(
              Yup.ref('callDurationMin'),
              'Max duration must be greater than Min duration'
            ),
      }),
    // Call Time: end must be after start
    callStartTime: Yup.mixed().nullable(),
    callEndTime: Yup.mixed()
      .nullable()
      .test('end-after-start', 'Call end time must be after start time', function (endValue) {
        const { callStartTime } = this.parent;
        if (!callStartTime || !endValue) return true; // skip if either is empty
        return new Date(endValue) > new Date(callStartTime);
      }),
    // Adjusted counts — must select at least one when the parent action type is active
    adjustedAnswerCount: Yup.array().when('actionType', {
      is: (val) => val?.includes('answer'),
      then: (s) => s.min(1, 'Adjusted Answer Count: select at least one option'),
      otherwise: (s) => s.nullable(),
    }),
    adjustedNotAnswerCount: Yup.array().when('actionType', {
      is: (val) => val?.includes('unanswer'),
      then: (s) => s.min(1, 'Adjusted Not Answer Count: select at least one option'),
      otherwise: (s) => s.nullable(),
    }),
    adjustedBusyCount: Yup.array().when('actionType', {
      is: (val) => val?.includes('busy'),
      then: (s) => s.min(1, 'Adjusted Busy Count: select at least one option'),
      otherwise: (s) => s.nullable(),
    }),
    adjustedOtherCount: Yup.array().when('actionType', {
      is: (val) => val?.includes('other'),
      then: (s) => s.min(1, 'Adjusted Other Count: select at least one option'),
      otherwise: (s) => s.nullable(),
    }),
  });

  const methods = useForm({
    // Re-run resolver whenever countMode changes so the max-100 rule updates
    resolver: yupResolver(schema),
    defaultValues: {
      campaignIds: '',
      actionType: [],
      // Answer
      answerCount: '',
      adjustedAnswerCount: [],
      // Not Answer
      notAnswerCount: '',
      adjustedNotAnswerCount: [],
      // Busy
      busyCount: '',
      adjustedBusyCount: [],
      // Other
      otherCount: '',
      adjustedOtherCount: [],
      // Call Duration
      callDurationMin: '',
      callDurationMax: '',
      // DateTime
      campaignDateTime: null,
      callStartTime: null,
      callEndTime: null,
    },
  });

  const { handleSubmit, control, setValue, reset, watch } = methods;
  const values = watch();

  const actionTypeValues =
    useWatch({
      control,
      name: 'actionType',
    }) || [];

  // Whether any count action type is selected — used to show the global toggle
  const hasCountActionSelected = actionTypeValues.some((t) => COUNT_ACTION_TYPES.includes(t));

  const handleActionTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    setValue('actionType', typeof value === 'string' ? value.split(',') : value);
  };

  const handleReset = () => {
    setResetLoader(true);
    reset();
    setValue('_dtmfNodes', []);
    setSelectedIvr(null);
    setSelectedUser(null);
    setUserError('');
    setCountMode('count');
    setUpdateExistingRecords(false);
    setTimeout(() => {
      setResetLoader(false);
    }, 500);
  };
  
  // ── Payload Builder ──────────────────────────────────────────────────────
  const buildPayload = (data) => {
    // Campaign IDs: split by comma, trim, remove empty
    const campaignIds = data.campaignIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    const payload = { campaignIds, updateExistingRecords };

    if (actionTypeValues.includes('answer')) {
      payload.A_required = {
        current: "A",
        count: Number(data.answerCount),
        countMode,
        adjustedTo: data.adjustedAnswerCount || null,
      };
    }

    if (actionTypeValues.includes('unanswer')) {
      payload.NA_required = {
        current: "NA",
        count: Number(data.notAnswerCount),
        countMode,
        adjustedTo: data.adjustedNotAnswerCount || null,
      };
    }

    if (actionTypeValues.includes('other')) {
      payload.O_required = {
        current: "O",
        count: Number(data.otherCount),
        countMode,
        adjustedTo: data.adjustedOtherCount || null,
      };
    }

    if (actionTypeValues.includes('busy')) {
      payload.BZ_required = {
        current: "BZ",
        count: Number(data.busyCount),
        countMode,
        adjustedTo: data.adjustedBusyCount || null,
      };
    }

    if (actionTypeValues.includes('ivrUpdate') && selectedIvr) {
      payload.IVR_required = {
        ivrId: selectedIvr.ivr_id,
        ivrTitle: selectedIvr.ivr_title,
        ivrType: selectedIvr.ivr_type,
      };
    }

    if (actionTypeValues.includes('dtmfUpdate')) {
      const dtmfState = values._dtmfNodes || {};
      payload.DTMF_required = {
        updateExistingRecords: Boolean(dtmfState.updateExistingRecords),
        nodes: dtmfState.nodes || [],
      };
    }

    if (actionTypeValues.includes('callDuration')) {
      payload.CallDuration_required = {
        min: Number(data.callDurationMin),
        max: Number(data.callDurationMax),
        callStartTime: formatDateTime(data.callStartTime),
        callEndTime: formatDateTime(data.callEndTime),
      };
    }

    if (actionTypeValues.includes('campaignDateTime')) {
      payload.DateTime_required = {
        campaignDateTime: formatDateTime(data.campaignDateTime),
        callStartTime: formatDateTime(data.callStartTime),
        callEndTime: formatDateTime(data.callEndTime),
      };
    }

    return payload;
  };

  const onSubmit = async (data) => {
    if (!selectedUser) {
      setUserError('User is required. Please select a user.');
      return;
    }
    try {
      setLoader(true);
      const payload = buildPayload(data);
      payload.selectedUserId = selectedUser.userId
      console.log('Admin Setting Payload:', JSON.stringify(payload, null, 2));
      const { message } = await axiosPostCall('adminSettingUpdate', payload);
      renderSuccessMessage(message || 'Admin setting updated successfully');
      handleReset();
    } catch (error) {
      console.log('error in admin setting update', error);
      renderErrorMessage(error?.message || 'Network error!');
    } finally {
      setLoader(false);
    }
  };

  const renderActionCards = () => {
    const cards = [];

    // Non-count cards — rendered in order they appear in actionTypeValues
    const nonCountTypes = ['callDuration', 'ivrUpdate', 'campaignDateTime', 'dtmfUpdate'];
    nonCountTypes.forEach((type) => {
      if (!actionTypeValues.includes(type)) return;
      switch (type) {
        case 'callDuration':
          cards.push(<CallDurationUpdateCard key={type} />);
          break;
        case 'ivrUpdate':
          cards.push(
            <IvrUpdateCard
              key={type}
              selectedIvr={selectedIvr}
              onIvrSelect={setSelectedIvr}
              userId={selectedUser?.userId}
            />
          );
          break;
        case 'campaignDateTime':
          cards.push(<CampaignDateTimeUpdateCard key={type} />);
          break;
        case 'dtmfUpdate':
          cards.push(
            <DtmfUpdateCard
              key={type}
              campaignIdsRaw={values.campaignIds || ''}
              onNodesChange={(dtmfState) => setValue('_dtmfNodes', dtmfState)}
            />
          );
          break;
        default:
          break;
      }
    });

    // All count types go into ONE combined card
    const activeCountTypes = actionTypeValues.filter((t) => COUNT_ACTION_TYPES.includes(t));
    if (activeCountTypes.length > 0) {
      cards.push(
        <CountUpdateCard key="countCard" activeTypes={activeCountTypes} countMode={countMode} />
      );
    }

    return cards;
  };

  return (
    <Box
      sx={{
        width: '100%',
        p: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '84vh',
        gap: '1rem',
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, (error) => console.log(error))}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Admin Setting
            </Typography>
            <Typography variant="body1">
              Create and manage your admin settings here. Fill in the details below.
            </Typography>
          </Box>

          {/* User Selector */}
          <UserSelectCard
            selectedUser={selectedUser}
            onUserSelect={(user) => {
              setSelectedUser(user);
              if (user) setUserError('');
            }}
            error={userError}
          />

          <InputContainer sx={{ mt: 2 }}>
            {/* Campaign IDs — comma-separated */}
            <InputInnerContainer>
              <RHFTextField
                name="campaignIds"
                label="Campaign IDs (comma-separated)"
                placeholder="e.g. 101,102,103"
                fullWidth
                helperText="Enter multiple IDs separated by commas"
              />
            </InputInnerContainer>
            <InputInnerContainer>
              <RHFSelect
                name="actionType"
                label="Action Type"
                size="medium"
                placeholder={'Select field to change'}
                onChange={handleActionTypeChange}
                SelectProps={{
                  renderValue: (selected) => {
                    const selectedLabels = (selected || []).map(
                      (value) => ActionTypeOptions.find((opt) => opt.value === value)?.label
                    );
                    return selectedLabels.length > 3
                      ? `${selectedLabels.slice(0, 3).join(', ')}, (+${selectedLabels.length - 3} More)`
                      : selectedLabels.join(', ');
                  },
                }}
              >
                {ActionTypeOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={actionTypeValues.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </RHFSelect>
            </InputInnerContainer>
          </InputContainer>

          {/* Global: Include All Campaign Records */}
          {actionTypeValues.length > 0 && (
            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateExistingRecords}
                    onChange={(e) => setUpdateExistingRecords(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">Include All Campaign Records</Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}

          {/* Global Count / Percentage toggle */}
          {hasCountActionSelected && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
                Count Mode (all cards)
              </Typography>
              <ToggleButtonGroup
                value={countMode}
                exclusive
                onChange={(_, newMode) => {
                  if (newMode) setCountMode(newMode);
                }}
                size="small"
              >
                <ToggleButton value="count">Count</ToggleButton>
                <ToggleButton value="percent">Percentage</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                This mode applies to all count cards below. You cannot mix count and percent.
                {countMode === 'percent' && ' Value must be between 0 and 100.'}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>{renderActionCards()}</Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <LoadingButton
              type="button"
              color="error"
              variant="contained"
              loading={resetloader}
              onClick={handleReset}
              sx={{ width: '120px' }}
            >
              Reset
            </LoadingButton>
           {checkButtonAccess("voiceAdminSetting", "w")  && <LoadingButton type="submit" variant="contained" loading={loader} sx={{ width: '120px' }}>
              Submit
            </LoadingButton>}
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
}
